import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, CalendarPlus, BellRing, UserCheck } from "lucide-react";
import { api, CITIES, SEO_CITIES, citySlug } from "@/lib/api";
import { HostessCard } from "@/components/HostessCard";
import { Seo } from "@/components/Seo";

const HERO = "https://images.unsplash.com/photo-1613915617430-8ab0fd7c6baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const STEPS = [
  { icon: CalendarPlus, t: "Pubblica l'evento", d: "Data, città, compenso e requisiti. Pochi minuti per raggiungere tutto il network." },
  { icon: BellRing, t: "Ricevi le disponibilità", d: "Ogni hostess disponibile si candida e tu ricevi una notifica immediata." },
  { icon: UserCheck, t: "Scegli la tua compagnia", d: "Valuta profili e gallerie, seleziona le professioniste ideali." },
];

export default function Landing() {
  const [city, setCity] = useState("");
  const [featured, setFeatured] = useState([]);
  const nav = useNavigate();

  useEffect(() => { api.get("/hostesses", { params: { sort: "newest", with_photo: true } }).then((r) => setFeatured(r.data.slice(0, 4))); }, []);

  return (
    <main data-testid="landing-page">
      <Seo title="YourHostess | Hostess, wingwoman e accompagnatrici a Milano, Varese, Roma e Napoli" description="Trova hostess, wingwoman e accompagnatrici a Milano, Varese, Roma e Napoli per eventi, fiere, cene e serate. Pubblica il tuo annuncio su YourHostess e ricevi subito i contatti." />
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E] via-[#0B0C0E]/85 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-24 w-full">
          <p className="eyebrow fade-up">Agenzia digitale · Hostess & Modelle</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6 max-w-2xl fade-up" style={{ animationDelay: "120ms" }}>
            Il volto perfetto <em className="text-[#D4AF37]">per il tuo evento o appuntamento</em> in Italia.
          </h1>
          <p className="text-base text-neutral-300 mt-6 max-w-lg leading-relaxed fade-up" style={{ animationDelay: "220ms" }}>
            Cerca Hostess, modelle e wingwoman nella tua città, oppure pubblica il tuo evento e ricevi le loro candidature.
          </p>
          <form onSubmit={(e) => { e.preventDefault(); nav(`/cerca${city ? `?city=${encodeURIComponent(city)}` : ""}`); }}
            className="mt-10 flex flex-col sm:flex-row max-w-xl bg-[#121418]/80 backdrop-blur-xl border border-white/10 fade-up" style={{ animationDelay: "320ms" }} data-testid="hero-search-form">
            <div className="flex items-center gap-3 px-5 flex-1">
              <Search className="w-4 h-4 text-[#D4AF37]" />
              <input value={city} onChange={(e) => setCity(e.target.value)} list="cities" placeholder="In quale città? Es. Milano"
                className="bg-transparent w-full py-4 text-sm focus:outline-none placeholder:text-neutral-500" data-testid="hero-city-input" />
              <datalist id="cities">{CITIES.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <button className="btn-gold" data-testid="hero-search-button">Cerca</button>
          </form>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 fade-up" style={{ animationDelay: "420ms" }}>
            <Link to="/accedi?mode=register&role=client" className="text-xs uppercase tracking-[0.18em] text-neutral-300 hover:text-[#D4AF37] flex items-center gap-2" data-testid="hero-client-cta">Crea il tuo evento <ArrowRight className="w-3 h-3" /></Link>
            <Link to="/accedi?mode=register&role=hostess" className="text-xs uppercase tracking-[0.18em] text-neutral-300 hover:text-[#D4AF37] flex items-center gap-2" data-testid="hero-hostess-cta">Sei una hostess? Unisciti <ArrowRight className="w-3 h-3" /></Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-28">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <p className="eyebrow">In evidenza</p>
            <h2 className="font-serif text-3xl lg:text-4xl mt-3">Hostess per te</h2>
          </div>
          <Link to="/cerca" className="btn-ghost !px-5 !py-2.5 hidden sm:inline-flex" data-testid="featured-view-all">Vedi tutte</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="featured-grid">
          {featured.map((h, i) => <HostessCard key={h.id} h={h} index={i} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-28" data-testid="how-it-works-section">
        <img src="/come-funziona.jpg?v=2" alt="Come ottenere un appuntamento con una wingwoman: 1 crea l'annuncio, 2 ricevi i contatti, 3 scegli la persona ideale" loading="lazy" width="1408" height="768"
          className="w-full max-w-4xl mx-auto h-auto" data-testid="how-it-works-image" />
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-36 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="eyebrow" data-testid="steps-eyebrow">Per te o la tua azienda</p>
          <h2 className="font-serif text-3xl lg:text-4xl mt-3 leading-tight" data-testid="steps-heading">Cerca, trova, contatta.</h2>
          <Link to="/accedi?mode=register&role=client" className="btn-gold mt-8" data-testid="steps-cta">Inizia ora</Link>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {STEPS.map(({ icon: Icon, t, d }, i) => (
            <div key={t} className="bg-[#0B0C0E] p-8">
              <span className="font-mono text-xs text-neutral-600">0{i + 1}</span>
              <Icon className="w-6 h-6 text-[#D4AF37] mt-6" strokeWidth={1.4} />
              <h3 className="font-serif text-2xl mt-5">{t}</h3>
              <p className="text-sm text-neutral-400 mt-3 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-36">
        <div className="border border-[#D4AF37]/20 bg-[#121418] p-10 lg:p-16 flex flex-col lg:flex-row lg:items-center justify-between gap-8 shadow-[0_0_25px_rgba(212,175,55,0.08)]">
          <div>
            <p className="eyebrow">Per hostess & modelle</p>
            <h2 className="font-serif text-3xl lg:text-4xl mt-3 max-w-xl">Crea il tuo book digitale e candidati agli eventi migliori.</h2>
          </div>
          <Link to="/accedi?mode=register&role=hostess" className="btn-gold shrink-0" data-testid="join-hostess-cta">Crea il tuo profilo</Link>
        </div>
      </section>

      <nav className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 text-xs text-neutral-500 leading-relaxed" data-testid="home-city-links">
        Hostess, wingwoman e accompagnatrici a{" "}
        {SEO_CITIES.map((c, i) => (
          <span key={c}>{i > 0 && " · "}<Link to={`/hostess-a/${citySlug(c)}`} className="text-neutral-400 hover:text-[#D4AF37] transition-colors" data-testid={`home-city-link-${citySlug(c)}`}>{c}</Link></span>
        ))}
      </nav>

    </main>
  );
}
